import { useEffect } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

const STORAGE_KEY = 'backstage:user-email-submitted:v1';

/**
 * On first render after sign-in, send the current user's email (sourced from
 * `identityApi.getProfileInfo()`, originally provided by Microsoft Entra at
 * sign-in) to the user-emails backend so it can be merged into the user's
 * catalog entity.
 *
 * Runs at most once per browser per user (gated by localStorage), and
 * silently no-ops if the identity has no email or the request fails.
 */
export const useSubmitEmailOnce = (): void => {
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  useEffect(() => {
    const controller = new AbortController();

    const run = async () => {
      const profile = await identityApi.getProfileInfo();
      const email = profile.email?.trim().toLowerCase();
      if (!email) return;

      const { userEntityRef } = await identityApi.getBackstageIdentity();
      const flagKey = `${STORAGE_KEY}:${userEntityRef}:${email}`;
      if (localStorage.getItem(flagKey)) return;

      const baseUrl = await discoveryApi.getBaseUrl('user-emails');
      const res = await fetchApi.fetch(`${baseUrl}/me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        signal: controller.signal,
      });

      if (res.ok) {
        localStorage.setItem(flagKey, String(Date.now()));
      }
    };

    run().catch(() => {
      /* silent — non-critical background sync (includes AbortError on unmount) */
    });

    return () => {
      controller.abort();
    };
  }, [identityApi, discoveryApi, fetchApi]);
};
